import { Loading } from "@xp-design/mobile";

// -转驼峰
export function underscoreToCamelCase(str: string): string | undefined {
  if (!str) {
    return undefined;
  }
  return str
    .replace(/(-\w)/g, function (match) {
      return match[1].toUpperCase();
    })
    .replace(/^-/, "");
}

// 转成style对象
export function renderStyle(styleData: Array<any>) {
  const styles: any = {};
  // 遍历增加样式
  styleData.forEach((e) => {
    if (Array.isArray(e.attr)) {
      e.attr.forEach((attrItem, attrIndex) => {
        const camelCaseAttr = underscoreToCamelCase(attrItem);
        if (camelCaseAttr) {
          styles[camelCaseAttr] =
            e?.val[attrIndex]?.toString() + (e.unit || "");
        }
      });
    } else {
      const camelCaseAttr = underscoreToCamelCase(e.attr);
      if (camelCaseAttr) {
        styles[camelCaseAttr] = e?.val?.toString() + (e.unit || "");
      }
    }
  });
  return styles;
}

export const parseJson = (jsonStr: string, defaultValue = null) => {
  try {
    return JSON.parse(jsonStr);
  } catch (e) {
    console.error("Json解析出错:", e);
    return defaultValue;
  }
};

/**
 * 增强版安全链式查找工具
 * 支持：
 * - 单参数调用：.xxx(key值) → 按 key 字段匹配（默认）
 * - 双参数调用：.xxx(字段名, 字段值) → 按指定字段匹配
 * @param {any} data - 初始数据（支持null/undefined/空对象/空数组）
 * @returns {Proxy} 代理对象，支持链式调用
 */
export function enhancedSafeChainFind(data) {
  // 增强的入参校验：处理所有可能的空值情况
  if (
    data == null ||
    (typeof data === "object" && Object.keys(data).length === 0) ||
    (Array.isArray(data) && data.length === 0)
  ) {
    console.warn("enhancedSafeChainFind: 接收到空数据，返回安全代理", data);
  }

  // 空代理：用于异常场景，确保链式调用安全
  const emptyProxy = new Proxy(function () {}, {
    get(target, prop) {
      if (prop === "val") return null; // 取值返回null
      return emptyProxy; // 其他属性访问继续返回空代理
    },
    apply(target, thisArg, args) {
      return emptyProxy; // 函数调用返回空代理
    },
  });

  // 创建正常代理
  const createProxy = (currentData) => {
    // 增强的数据校验
    if (
      currentData == null ||
      (typeof currentData === "object" &&
        Object.keys(currentData).length === 0) ||
      (Array.isArray(currentData) && currentData.length === 0)
    ) {
      return emptyProxy;
    }

    return new Proxy(function () {}, {
      get(target, prop) {
        // 特殊属性：获取当前值
        if (prop === "val") return currentData;

        // 处理函数调用（支持单/双参数）
        const handler = (...args) => {
          try {
            // 参数校验
            if (!args || args.length === 0 || args.length > 2) {
              console.warn("enhancedSafeChainFind: 参数数量错误", args);
              return emptyProxy;
            }

            // 1. 解析参数：单参数用'key'字段，双参数用指定字段
            let field, value;
            if (args.length === 1) {
              field = "key"; // 默认字段为'key'
              value = args[0];
            } else if (args.length === 2) {
              [field, value] = args; // 双参数：字段名 + 字段值
            }

            // 值校验
            if (value === undefined || value === null) {
              console.warn("enhancedSafeChainFind: 查找值为空", {
                field,
                value,
              });
              return emptyProxy;
            }

            // 2. 获取当前层级的数组（从 currentData[prop] 中取）
            const currentArray = currentData[prop];
            if (!Array.isArray(currentArray) || currentArray.length === 0) {
              return emptyProxy;
            }

            // 3. 在数组中查找匹配项
            const foundItem = currentArray.find((item) => {
              if (item == null || typeof item !== "object") return false;
              return item[field] === value;
            });

            // 4. 进入下一层级
            return createProxy(foundItem);
          } catch (error) {
            console.error("链式查找出错：", error);
            return emptyProxy;
          }
        };

        // 让函数支持继续访问属性
        return new Proxy(handler, {
          get(target, nextProp) {
            if (nextProp === "val") {
              const propValue = currentData[prop];
              return propValue != null ? propValue : null;
            }
            const propValue = currentData[prop];
            return createProxy(propValue);
          },
        });
      },

      // 支持直接调用代理（如 enhancedSafeChainFind(array)('key值')）
      apply(target, thisArg, args) {
        try {
          const currentArray = currentData;
          if (!Array.isArray(currentArray) || currentArray.length === 0) {
            return emptyProxy;
          }

          // 参数校验
          if (!args || args.length === 0 || args.length > 2) {
            console.warn("enhancedSafeChainFind: 直接调用参数错误", args);
            return emptyProxy;
          }

          // 解析参数（同上）
          let field, value;
          if (args.length === 1) {
            field = "key";
            value = args[0];
          } else if (args.length === 2) {
            [field, value] = args;
          }

          // 值校验
          if (value === undefined || value === null) {
            console.warn("enhancedSafeChainFind: 直接调用查找值为空", {
              field,
              value,
            });
            return emptyProxy;
          }

          const foundItem = currentArray.find((item) => {
            if (item == null || typeof item !== "object") return false;
            return item[field] === value;
          });

          return createProxy(foundItem);
        } catch (error) {
          console.error("直接调用链式查找出错：", error);
          return emptyProxy;
        }
      },
    });
  };

  return createProxy(data);
}

/**
 * 根据指定字段查找数组中的对象
 * @param {Array} array - 要查找的数组
 * @param {string} field - 字段名（默认为'key'）
 * @param {any} value - 字段值
 * @returns {any} 找到的对象，没找到返回null
 */
export function getValueByKey(
  array: any[],
  field: string = "key",
  value?: any,
): any {
  // 如果只传了两个参数，说明第二个参数是value，field使用默认值'key'
  if (arguments.length === 2) {
    value = field;
    field = "key";
  }

  // 参数验证
  if (!Array.isArray(array) || array.length === 0) {
    return null;
  }

  if (value === undefined || value === null) {
    return null;
  }

  // 查找匹配的对象
  return (
    array.find((item) => {
      if (item == null) return false;
      return item[field] === value;
    }) || null
  );
}
